import {FC, useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';
//mui
import {Box, useMediaQuery} from '@mui/material';
//store
import {getCurrentEventSelector, getEventsSelector} from '@/store/organization-event';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {
  clearTablesAndTicketsData,
  clearTicketsTiers,
  getTablesAndTicketsStatisticsSelector,
  getTicketsList,
  getTicketsStatistics,
  getTicketsTiers,
} from '@/store/organization-tables';
//components
import StatisticTablesSold from '@/components/Organization/Dashboard/Statistics/StatisticsInfo/StatisticsInfoTables/StatisticTablesSold';
import StatisticAvailableTickets from '@/components/Organization/TablesAndTickets/StatisticAvailableTickets';
import TablesAndTicketsTable from '@/components/Organization/TablesAndTickets/TablesAndTicketsTable';
import TableStatistics from '@/shared/components/Table/TableStatistics';
import EmptyContent from '@/shared/components/Table/EmptyContent';
import Modal from '@/shared/components/Modal';
import Loader from '@/shared/components/Loader';
//helpers
import {getDefaultRequestBody, getErrorMessage} from '@/utils/helpers';
//icons
import {ReactComponent as StatisticsTicketIcon} from '@/assets/icons/statistics-ticket.svg';
import {ReactComponent as StatisticsDollarIcon} from '@/assets/icons/statistics-dollar.svg';
import {ReactComponent as TicketIcon} from '@/assets/icons/sidebar/ticket.svg';
//constants
import {AVAILABLE_TICKETS, defaultPageSize, EVENT_ID, TABLES_SOLD, VIEW_DETAILS} from '@/utils/constants';
//enums
import {STATISTIC} from '@/utils/enums/tables.enum';
//styles
import {tablesAndTicketsStyle} from './TablesAndTickets.styles';
import {orgAPI} from '@/core/api';

const TablesAndTickets: FC = () => {
  const dispatch = useAppDispatch();

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const [activeStatisticsInfoTitle, setActiveStatisticsInfoTitle] = useState<string>('');
  const [isTableInfoModalOpen, setIsTableInfoModalOpen] = useState(false);

  const events = useAppSelector(getEventsSelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const tableStatistics = useAppSelector(getTablesAndTicketsStatisticsSelector);

  const isLaptop = useMediaQuery('(min-width:1200px)');

  useEffect(() => {
    if (!currentEvent?.id) {
      if (events && !events.length) setIsDataLoaded(true);
      return;
    }
    const getTableData = async () => {
      setIsDataLoaded(false);

      try {
        const reqBody = getDefaultRequestBody(currentEvent!.id, EVENT_ID);

        await Promise.all([
          dispatch(
            getTicketsList({
              reqBody: {
                ...reqBody,
                page: 1,
                pageSize: isLaptop ? defaultPageSize : 10000,
              },
              apiInstance: orgAPI,
            })
          ).unwrap(),
          dispatch(getTicketsStatistics(currentEvent.id)).unwrap(),
          ...(currentEvent.tableInfos.length
            ? [dispatch(getTicketsTiers({reqBody: currentEvent.id, apiInstance: orgAPI})).unwrap()]
            : [Promise.resolve(dispatch(clearTicketsTiers()))]),
        ]);
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setIsDataLoaded(true);
      }
    };

    getTableData();
  }, [currentEvent?.id, events?.length, currentEvent?.tableInfos?.length, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearTablesAndTicketsData());
    };
  }, [dispatch]);

  const availableTicketsTitle = useMemo(
    () => `${tableStatistics?.availableTickets} of ${tableStatistics?.totalTickets}`,
    [tableStatistics]
  );

  const tablesSoldTitle = useMemo(
    () => `${tableStatistics?.tablesSold} of ${tableStatistics?.allTables}`,
    [tableStatistics]
  );

  const handleCloseTableInfoModal = () => setIsTableInfoModalOpen(false);

  const activeStatisticsTable = useMemo(() => {
    switch (activeStatisticsInfoTitle) {
      case STATISTIC.TABLES_SOLD:
        return <StatisticTablesSold />;

      case STATISTIC.AVAILABLE_TICKETS:
        return <StatisticAvailableTickets />;

      default:
        break;
    }
  }, [activeStatisticsInfoTitle]);

  const handleStatisticsClick = (title: string) => {
    setActiveStatisticsInfoTitle(title);
    setIsTableInfoModalOpen(true);
  };

  return (
    <Box sx={tablesAndTicketsStyle.wrapper}>
      {isDataLoaded ? (
        !!events?.length && currentEvent ? (
          <Box>
            <Box sx={tablesAndTicketsStyle.content}>
              <TableStatistics
                icon={StatisticsTicketIcon}
                title={availableTicketsTitle}
                description={AVAILABLE_TICKETS}
                onClick={handleStatisticsClick}
                actionsData={[VIEW_DETAILS]}
              />
              <TableStatistics
                icon={StatisticsDollarIcon}
                title={tablesSoldTitle}
                description={TABLES_SOLD}
                onClick={handleStatisticsClick}
                actionsData={[VIEW_DETAILS]}
              />
            </Box>

            <TablesAndTicketsTable />

            <Modal
              title={activeStatisticsInfoTitle}
              onClose={handleCloseTableInfoModal}
              open={isTableInfoModalOpen}
              sx={tablesAndTicketsStyle.modal}
            >
              {activeStatisticsTable}
            </Modal>
          </Box>
        ) : (
          <EmptyContent title="You do not have any tables or tickets." icon={TicketIcon} />
        )
      ) : (
        <Loader parentClassName={tablesAndTicketsStyle.loader} size={50} />
      )}
    </Box>
  );
};

export default TablesAndTickets;
